'use client';

import { useMemo, useState } from 'react';
import {
  getCountries,
  getCountryCallingCode,
  type Country,
} from 'react-phone-number-input';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface PhoneNumberInputProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  countryLabel: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  locale?: string;
  defaultCountry?: Country;
  allowedCountries?: Country[];
  size?: 'sm' | 'md' | 'lg';
  flat?: boolean;
}

function getCountryFlag(country: Country) {
  return country
    .toUpperCase()
    .replace(/./g, (character) => String.fromCodePoint(127397 + character.charCodeAt(0)));
}

export function PhoneNumberInput({
  value,
  onChange,
  label,
  countryLabel,
  placeholder,
  error,
  disabled,
  locale = 'en',
  defaultCountry = 'CN',
  allowedCountries,
  size = 'lg',
  flat = false,
}: PhoneNumberInputProps) {
  const [country, setCountry] = useState<Country>(defaultCountry);
  const countries = useMemo(
    () => allowedCountries ?? getCountries(),
    [allowedCountries],
  );
  const displayNames = useMemo(
    () => new Intl.DisplayNames([locale === 'jp' ? 'ja' : locale], { type: 'region' }),
    [locale],
  );
  const callingCode = getCountryCallingCode(country);
  const nationalNumber = value.startsWith(`+${callingCode}`)
    ? value.slice(callingCode.length + 1)
    : value.replace(/\D/g, '');

  const handleCountryChange = (nextCountry: Country) => {
    setCountry(nextCountry);
    const nextCallingCode = getCountryCallingCode(nextCountry);
    onChange(nationalNumber ? `+${nextCallingCode}${nationalNumber}` : '');
  };

  const handleNationalNumberChange = (nextValue: string) => {
    const digits = nextValue.replace(/\D/g, '');
    onChange(digits ? `+${callingCode}${digits}` : '');
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
        {label}
      </label>
      <div className="flex gap-2">
        {countries.length > 1 ? (
          <Select value={country} onValueChange={(next) => handleCountryChange(next as Country)} disabled={disabled}>
            <SelectTrigger
              className={cn('shrink-0', size === 'lg' && 'h-10', flat && 'shadow-none')}
              aria-label={countryLabel}
            >
              <SelectValue>
                <span aria-hidden="true">{getCountryFlag(country)}</span>
                <span className="tabular-nums">+{callingCode}</span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent position="popper" align="start" className="max-h-72">
              {countries.map((item) => (
                <SelectItem key={item} value={item}>
                  <span aria-hidden="true">{getCountryFlag(item)}</span>
                  <span>{displayNames.of(item) ?? item}</span>
                  <span className="text-muted-foreground tabular-nums">+{getCountryCallingCode(item)}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div className={cn(
            'flex shrink-0 items-center text-sm',
            size === 'lg' ? 'h-10' : size === 'sm' ? 'h-8' : 'h-9',
          )}>
            <span className="tabular-nums">+{callingCode}</span>
          </div>
        )}
        <Input
          type="tel"
          aria-label={label}
          inputMode="tel"
          autoComplete="tel-national"
          value={nationalNumber}
          placeholder={placeholder}
          disabled={disabled}
          error={error}
          size={size}
          className={cn('tabular-nums', flat && 'shadow-none')}
          onChange={(event) => handleNationalNumberChange(event.target.value)}
        />
      </div>
      {error ? <p role="alert" className="text-pretty text-xs text-red-600 dark:text-red-400">{error}</p> : null}
    </div>
  );
}
